import { PHOTO_CHUNK_SIZE, chunkCount, type PhotoKind } from '@pms/shared';

import { db } from '../sync/db.js';
import type { PreparedPhoto } from './capture.js';
import type { PhotoRecord } from './types.js';

/**
 * Очередь фотографий — отдельная от очереди событий и с низким приоритетом.
 * Отчёт считается сданным сразу после отправки цифр, фото могут догружаться
 * хоть два часа.
 */

export interface AddPhotoInput {
  reportId: string;
  /** Что на снимке: продукция или переданное рабочее место. */
  kind: PhotoKind;
  siteId: string;
  machineId?: string | null;
  shiftNumber: number;
  takenAt: Date;
  prepared: PreparedPhoto;
}

export async function addPhoto(input: AddPhotoInput): Promise<PhotoRecord> {
  const record: PhotoRecord = {
    id: crypto.randomUUID(),
    reportId: input.reportId,
    kind: input.kind,
    siteId: input.siteId,
    machineId: input.machineId ?? null,
    shiftNumber: input.shiftNumber,
    takenAt: input.takenAt.toISOString(),
    blob: input.prepared.blob,
    bytes: input.prepared.bytes,
    width: input.prepared.width,
    height: input.prepared.height,
    sha256: input.prepared.sha256,
    status: 'pending',
    chunkSize: PHOTO_CHUNK_SIZE,
    totalChunks: chunkCount(input.prepared.bytes, PHOTO_CHUNK_SIZE),
    uploadedChunks: 0,
    attempts: 0,
    createdAt: new Date().toISOString(),
  };
  await db.photos.add(record);
  return record;
}

/** Следующее фото на отправку. Порядок — по времени съёмки. */
export async function nextPhoto(): Promise<PhotoRecord | undefined> {
  const waiting = await db.photos
    .where('status')
    .anyOf('pending', 'uploading')
    .sortBy('createdAt');
  return waiting[0];
}

export async function markUploading(
  id: string,
  patch: { chunkSize: number; totalChunks: number; uploadedChunks: number },
): Promise<void> {
  const record = await db.photos.get(id);
  if (!record) return;
  await db.photos.put({ ...record, ...patch, status: 'uploading', lastError: undefined });
}

export async function setProgress(id: string, uploadedChunks: number): Promise<void> {
  const record = await db.photos.get(id);
  if (!record) return;
  await db.photos.put({ ...record, uploadedChunks });
}

/**
 * Сервер подтвердил приём — только теперь снимок удаляется с устройства.
 * Метаданные остаются: по ним видно, что фото было и когда ушло.
 */
export async function markUploaded(id: string): Promise<void> {
  const record = await db.photos.get(id);
  if (!record) return;
  const { blob: _blob, ...rest } = record;
  await db.photos.put({
    ...rest,
    status: 'uploaded',
    uploadedChunks: record.totalChunks,
    uploadedAt: new Date().toISOString(),
    lastError: undefined,
  });
}

/** Временная неудача: снимок остаётся на устройстве, попробуем позже. */
export async function markRetryable(id: string, error: string): Promise<void> {
  const record = await db.photos.get(id);
  if (!record) return;
  await db.photos.put({
    ...record,
    status: 'pending',
    attempts: record.attempts + 1,
    lastError: error,
  });
}

/** Сервер отказал по существу — нужен человек. Снимок не удаляем. */
export async function markFailed(id: string, error: string): Promise<void> {
  const record = await db.photos.get(id);
  if (!record) return;
  await db.photos.put({ ...record, status: 'failed', lastError: error });
}

export async function retryPhoto(id: string): Promise<void> {
  const record = await db.photos.get(id);
  if (!record || record.status === 'uploaded') return;
  await db.photos.put({ ...record, status: 'pending', attempts: 0, lastError: undefined });
}

export async function listByReport(reportId: string): Promise<PhotoRecord[]> {
  return db.photos.where('reportId').equals(reportId).sortBy('createdAt');
}

export async function countWaiting(): Promise<number> {
  return db.photos.where('status').anyOf('pending', 'uploading').count();
}

export async function countFailed(): Promise<number> {
  return db.photos.where('status').equals('failed').count();
}

/** Статус фото у смены — его видит администратор в списке отчётов. */
export async function reportPhotoStatus(
  reportId: string,
): Promise<'NONE' | 'PENDING' | 'UPLOADED'> {
  const photos = await listByReport(reportId);
  if (photos.length === 0) return 'NONE';
  return photos.every((photo) => photo.status === 'uploaded') ? 'UPLOADED' : 'PENDING';
}

export async function listRecentPhotos(limit = 50): Promise<PhotoRecord[]> {
  return db.photos.orderBy('createdAt').reverse().limit(limit).toArray();
}
